import { computed, observable, toJS } from 'mobx';
import { BaseStore, DataStore } from '.';
import { Stores } from '../models/Stores';
import { models, query, utils } from '@kurtosys/ksys-app-template/dist';
import { GlobalDisclaimerStore } from './GlobalDisclaimerStore';
import { TranslationStore } from './TranslationStore';
const Query = query.Query;

// Adding basic type for now, instead of using any, for the most part we are only passing in a single level to this object within the FC
type QueryMime = Record<
    string,
    models.query.IQueryOptions | Record<string, models.query.IQueryOptions>
>;

export class QueryStore extends BaseStore {
    @observable.ref
    defaultContext: models.query.IQueryContext;

    constructor(stores: Stores) {
        super(stores);
    }

    get translationStore(): TranslationStore {
        return this.stores.translation;
    }

    get dataStore(): DataStore {
        return this.stores.data;
    }

    get inputs() {
        return this.dataStore.appDataProps;
    }

    get culture() {
        return this.dataStore.culture;
    }

    @computed
    get executionOptions(): models.query.IExecutionOptions {
        return this.getExecutionOptionsUsingContext({});
    }

    async initialize() {
        // this.loadDefaultContext();
    }

    getExecutionOptionsUsingContext(
        context: models.query.IQueryContext
    ): models.query.IExecutionOptions {
        const globalQueries =
            (this.dataStore &&
                this.dataStore.config &&
                this.dataStore.config.globalConfig &&
                this.dataStore.config.globalConfig.globalQueries) ||
            [];
        return {
            context: {
                globalQueries: toJS(globalQueries),
                ...this.defaultContext,
                ...context,
            },
            fallbackCulture: 'en-GB',
            culture: this.culture,
            inputs: this.inputs,
            translate: this.translationStore.translate,
            // TODO: add below items to completely integrate with Query options (has not been used previously)
            // applicationGuid: appStore.applicationGuid,
            // defaultContextParentKey: this.defaultContextParentKey,
            // responseContext: this.responseContext,
            // fundManagerContext: this.fundManagerContext,
            // statisticContext: this.statisticContext,
            // serviceUrl: appStore.serviceUrl,
            // globalFormatsByKey: appStore.globalFormatsByKey,
        };
    }

    loadDefaultContext = () => {
        const defaultContext: models.query.IQueryContext = {
            globalDisclaimers: GlobalDisclaimerStore.rawDisclaimers.map(d => {
                return {
                    disclaimerType: d.disclaimerType,
                    disclaimers: d.disclaimers,
                } as models.api.dictionary.IDisclaimer;
            }),
            globalCommentaries: this.stores.commentary.rawCommentaries.map(
                c => {
                    return {
                        commentaryType: c.commentaryType,
                        commentaries: c.commentaries,
                    } as any;
                }
            ),
        };
        this.defaultContext = defaultContext;
    };

    getValueFromQuery = (
        queryOptions: models.query.IQueryOptions,
        queryContext: models.query.IQueryContext
    ): string | undefined => {
        if (queryOptions) {
            const { queryStore } = this.dataStore;
            const executionOptions = queryStore.getExecutionOptionsUsingContext(
                queryContext
            );
            return queryStore.execute(queryOptions, {
                ...executionOptions,
                responseContext: queryContext.response,
            });
        }
        return undefined;
    };

    query(
        options?: models.query.IQueryOptions,
        context?: models.query.IQueryContext
    ) {
        const overrideExecutionOptions = this.getExecutionOptionsUsingContext(
            context
        );
        return this.execute(options, overrideExecutionOptions);
    }

    queryOrDefault(
        options: models.query.IQueryOptions,
        context?: models.query.IQueryContext,
        defaultValue?: any
    ) {
        const overrideExecutionOptions = this.getExecutionOptionsUsingContext(
            context
        );
        const response = this.execute(options, overrideExecutionOptions);
        if (response) {
            return response;
        }
        return defaultValue;
    }

    execute(
        options: models.query.IQueryOptions,
        executionOptions: models.query.IExecutionOptions
    ) {
        if (options) {
            const queryInstance = new Query(
                options,
                this.translationStore.translationHelper
            );
            return queryInstance.execute(executionOptions);
        }
    }

    getQueryResults(
        context: models.query.IQueryContext,
        queries: models.query.IQueryKeyValue,
        fundManager?: models.api.dictionary.IFundManager,
        documentEntityKeys?: models.query.IQueryContextConfigurationDocumentEntityKey,
        translateFn?: (key: string, placeholders?: any) => string
    ) {
        if (context && queries) {
            const defaultExecutionOptions = this.getExecutionOptionsUsingContext(
                context
            );
            const executionOptions: models.query.IExecutionOptions = {
                ...defaultExecutionOptions,
                fundManagerContext: fundManager,
                contextConfiguration: {
                    documentEntityKeys,
                },
            };
            const queriesObj = JSON.parse(JSON.stringify(queries));

            // Translate number thresholds before using them in the query format
            if (translateFn) {
                Object.keys(queriesObj).forEach(queryKey => {
                    const { format } = queriesObj[queryKey];
                    if (
                        format &&
                        format.options &&
                        format.options.numberThresholds
                    ) {
                        format.options.numberThresholds.forEach(
                            numberThreshold => {
                                numberThreshold.text = translateFn(
                                    numberThreshold.text
                                );
                            }
                        );
                    }
                });
            }
            return this.executeQueryMime(queriesObj, executionOptions);
        }
        return {};
    }

    executeQueryMime(
        queryMime: QueryMime,
        executionOptions: models.query.IExecutionOptions = this.executionOptions
    ) {
        if (queryMime) {
            return query.Query.convertQueryMime(queryMime, executionOptions, {
                translationHelper: this.translationStore.translationHelper,
            });
        }
    }

    mergeQueriesAndProps<T>(
        componentOrchestration: { props: any; queries: any },
        executionOptions: models.query.IExecutionOptions = this.executionOptions
    ): T | undefined {
        if (componentOrchestration) {
            const { props, queries } = componentOrchestration;
            const queryResults = queries
                ? this.executeQueryMime(queries, executionOptions)
                : {};
            const response: T = utils.object.deepMergeObjectsWithOptions(
                { arrayMergeStrategy: 'DeepMerge' },
                props || {},
                queryResults
            );
            return response;
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/QueryStore.ts